// La disposición de la oficina en escritorio e iPad (S11, brief §5 + crítica A8/M7): la oficina de monigotes detrás de
// todo (mundo/Mundo.tsx), los widgets en su franja de arriba y, flotando encima con la misma superficie y el mismo margen
// (16 px): el panel del equipo a la izquierda (con «Contratar» en su cabecera: un solo sitio) y, si hay un chat abierto,
// su ventana ocupando el resto, pegada al panel a 16 px, con el mundo atenuado detrás.
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import type { ContratadoDTO, SalaDTO } from '../compartido/vocabulario';
import { Mundo } from '../mundo/Mundo';
import { aMarcadorMundo } from '../mundo/marcador';
import { marcadorEscena } from './situacion';
import { useEquipo } from './useEquipo';
import { bandejaDe, miBandeja } from './bandejas';
import { ListaEquipo } from './ListaEquipo';
import { RIEL_PX, RielEquipo } from './RielEquipo';
import { ANCHO_PLEGADO_PX, useOficinaStore } from '../store';
import type { OrdenMesas } from '../api';

export const MARGEN_PX = 16;
export const PANEL_PX = 336;

/** Lo que ocupa a la izquierda lo que flota (lista abierta o plegada en columna de caras), con sus márgenes. */
export function useIzquierdaPx(): number {
  const plegado = useOficinaStore((s) => s.panelPlegado);
  return MARGEN_PX + (plegado ? RIEL_PX : PANEL_PX) + MARGEN_PX;
}

/**
 * Lo que la PLANTA de la oficina deja libre a la izquierda: lo que ocuparía la lista con el ancho de la ventana, no lo que
 * ocupa ahora. Plegar o desplegar la lista no re-maqueta la oficina: se queda donde está, como cuando se oculta el Dock
 * (flujo.md #5). Solo el ancho de la ventana (que decide si la lista empieza plegada) la cambia.
 */
function useReservaPlanoPx(): number {
  const [ancho, setAncho] = useState(() => window.innerWidth);
  useEffect(() => {
    const alCambiar = () => setAncho(window.innerWidth);
    window.addEventListener('resize', alCambiar);
    return () => window.removeEventListener('resize', alCambiar);
  }, []);
  return MARGEN_PX + (ancho < ANCHO_PLEGADO_PX ? RIEL_PX : PANEL_PX) + MARGEN_PX;
}

export function Escenario({
  salas,
  contratados,
  agenteAbierto,
  alAbrir,
  alCerrarChat,
  alContratar,
  alAjustarSala,
  alOrdenarMesas,
  widgets,
  chat,
}: {
  salas: SalaDTO[];
  contratados: Record<string, ContratadoDTO>;
  agenteAbierto: string | null;
  alAbrir: (agentId: string) => void;
  alCerrarChat: () => void;
  alContratar: (salaId?: string, grupoId?: string) => void;
  alAjustarSala: (salaId: string) => void;
  /** Ordenar las mesas a mano (mover, poner, quitar). */
  alOrdenarMesas: (projectId: string, orden: OrdenMesas) => void;
  /** Rebrand 03-11: la fila de widgets (las pantallas de la pared) arriba de la oficina. */
  widgets?: ReactNode;
  chat: ReactNode;
}) {
  const izquierda = useIzquierdaPx();
  const reservaPlano = useReservaPlanoPx();
  const plegado = useOficinaStore((s) => s.panelPlegado);
  const plegarPanel = useOficinaStore((s) => s.plegarPanel);
  const ajustarPlegado = useOficinaStore((s) => s.ajustarPlegadoAlAncho);
  useEffect(() => {
    const alCambiar = () => ajustarPlegado(window.innerWidth);
    window.addEventListener('resize', alCambiar);
    return () => window.removeEventListener('resize', alCambiar);
  }, [ajustarPlegado]);
  const [sobre, setSobre] = useState<string | null>(null);
  // 04-11 — los widgets tienen su franja arriba y la oficina empieza debajo (si flotaran encima taparían mesas y
  // letreros en pantallas medianas y en el iPad): son la pared (64 px, ui/index.tsx `WIDGET`). Se mide su alto real
  // (0 si ninguno tiene datos).
  const filaWidgets = useRef<HTMLDivElement>(null);
  const [altoWidgets, setAltoWidgets] = useState(0);
  // Depende de si HAY widgets, no del elemento (uno nuevo en cada render): si no, se desconectaba y reconectaba siempre.
  const hayWidgets = !!widgets;
  useEffect(() => {
    const el = filaWidgets.current;
    if (!el) return;
    const medir = () => setAltoWidgets(el.offsetHeight);
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, [hayWidgets]);
  const arriba = hayWidgets && altoWidgets ? MARGEN_PX + altoWidgets : 0;
  // 06-10 — las mesas son de todos: en la escena salen también los agentes de los demás, apagados, con de quién
  // son y sin avisos (lo que les toca es de su dueño; la lista de la izquierda es la bandeja de quien mira).
  const mia = miBandeja(useOficinaStore((s) => s.yo));
  const grupos = useOficinaStore((s) => s.grupos);
  // Las subidas y la hora del equipo: las mismas que usa la lista (useEquipo), para que la mesa diga lo mismo que la fila
  // (una subida pendiente lleva su insignia; un aparcado duerme).
  const { santi, subidas, ahora } = useEquipo(contratados);
  const marcador = useCallback(
    (c: ContratadoDTO) => {
      if (bandejaDe(c) === mia) return aMarcadorMundo(marcadorEscena(c, santi.get(c.agentId), { subida: subidas.get(c.agentId) ?? null, ahora }));
      return { destaca: false, icono: null, insignia: null, punto: null, texto: marcadorEscena(c).texto, de: (c.dueno?.nombre ?? 'Pau').split(/\s+/)[0] };
    },
    [mia, santi, subidas, ahora],
  );
  return (
    <div className="relative h-dvh overflow-hidden bg-oficina-fondo font-sans text-oficina-texto antialiased">
      {/* 04-11 (Pau: «me convencía más con los monigotes en mesas; así me cuesta encontrar mis trabajos»): vuelve la
          oficina con cada agente en su mesa, como la pantalla de inicio del iPhone: cada cosa donde Pau la puso y lo que
          le necesita, marcado sin moverse de sitio. Lo ordenado por estado es la lista de la izquierda. */}
      <div className="absolute inset-x-0 bottom-0" style={{ top: arriba }}>
        <Mundo
          salas={salas}
          contratados={contratados}
          grupos={grupos}
          panelIzqPx={izquierda}
          reservaIzqPx={reservaPlano}
          // Con un chat abierto el mundo queda atenuado detrás: ninguna fila de la lista le saca su tarjeta.
          resaltado={agenteAbierto ? null : sobre}
          // 04-11 — el agente abierto (lista, Alt+↓, Ctrl+K): si su sala va plegada en carpeta, se abre y la vista va a su mesa.
          enfocado={agenteAbierto}
          alAbrir={alAbrir}
          alContratar={alContratar}
          alAjustarSala={alAjustarSala}
          alOrdenarMesas={alOrdenarMesas}
          // El mundo marca lo mismo que la lista («¿qué me toca?»), con su icono y su color.
          marcador={marcador}
        />
      </div>
      {/* Las pantallas de la pared, como widgets en su franja de arriba, a la derecha de la lista. Con un chat abierto
          se ocultan sin desmontarse (la oficina no salta de sitio detrás del chat). */}
      {widgets ? (
        <div ref={filaWidgets} className={cn('pointer-events-auto absolute z-10', agenteAbierto && 'invisible')} style={{ left: izquierda, right: MARGEN_PX, top: MARGEN_PX }}>
          {widgets}
        </div>
      ) : null}

      {agenteAbierto ? (
        <button type="button" tabIndex={-1} aria-label={t('oficinaV2.chat.cerrar')} onClick={alCerrarChat} className="absolute inset-0 z-20 cursor-default bg-black/50 animate-in fade-in-0" />
      ) : null}

      <div className="pointer-events-none absolute z-30 flex flex-col" style={{ left: MARGEN_PX, top: MARGEN_PX, bottom: MARGEN_PX, width: plegado ? RIEL_PX : PANEL_PX }}>
        <div className="pointer-events-auto flex max-h-full min-h-0 flex-col">
          {plegado ? (
            <RielEquipo contratados={contratados} abierto={agenteAbierto} alAbrir={alAbrir} alContratar={() => alContratar()} alDesplegar={() => plegarPanel(false)} alResaltar={setSobre} />
          ) : (
            <ListaEquipo contratados={contratados} abierto={agenteAbierto} alAbrir={alAbrir} alContratar={() => alContratar()} alResaltar={setSobre} alPlegar={() => plegarPanel(true)} />
          )}
        </div>
      </div>

      {agenteAbierto ? (
        <div className="absolute z-30" style={{ left: izquierda, right: MARGEN_PX, top: MARGEN_PX, bottom: MARGEN_PX }}>
          {chat}
        </div>
      ) : null}
    </div>
  );
}
